import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import { setCookie, getCookie } from 'modules/common/utils';
import { localViews } from 'modules/Auth/AuthProvider/views';
import { getAnonymousId, onTrackingScriptReady } from 'modules/common/components/AnalyticsTracking/trackingScript';

export const setAnalyticsUserId = (userId: number | null) => {
    const expireDate = new Date();
    const encodedUserId = btoa(userId ? userId.toString() : '');
    if (userId) {
        // 8h match the token expiration period
        expireDate.setTime(expireDate.getTime() + 8 * 60 * 60 * 1000);
        window.localStorage.setItem('cltv_user_id', encodedUserId);
    } else {
        expireDate.setTime(expireDate.getTime() - 1000);
        window.localStorage.removeItem('cltv_user_id');
    }
    setCookie('cltv_user_id', encodedUserId, expireDate, import.meta.env.VITE_TRACKING_SCRIPT_COOKIE_DOMAIN);
};

export const getAnalyticsUserId = () => {
    const userId = window.localStorage.getItem('cltv_user_id') || getCookie('cltv_user_id') || null;
    return userId ? decodeURIComponent(userId) : null;
};

export const collectiveTracking = {
    onReady: onTrackingScriptReady,
    track: <T = object>({ name, properties }: { name: string; properties?: T }) => {
        const props = { location: window.location.href, path: window.location.pathname, ...properties };
        collectiveTracking.sendEvent({ eventType: 'track', name, properties: props });
    },
    pageview: (properties = {}) => {
        collectiveTracking.sendEvent({ eventType: 'page', name: '', properties });
    },
    getAnonymousId,
    sendEvent: <T = object>({
        eventType,
        name = '',
        properties,
    }: {
        eventType: string;
        name?: string;
        properties?: T;
    }) => {
        try {
            // Nothing at all from an impersonated session. The impersonation JWT is minted for the
            // member, so the analytics id is the member's, but the event is assembled in the
            // operator's browser: its anonymous id, ip, userAgent, locale and timezone are the
            // operator's, and Segment writes them against the member. The events themselves describe
            // the operator's clicks, including writes the backend then refuses with
            // IMPERSONATION_READ_ONLY. They used to go through tagged `is_impersonating`, but the
            // hook layer overwrote `is_staff` with the member's own flag and no event named the
            // operator, so downstream could not reliably tell them apart from the member's own.
            if (localViews.isImpersonating()) {
                return;
            }

            const analyticsUserId = getAnalyticsUserId();

            // The tracking script promotes an identify's properties to Segment traits, and Segment
            // writes traits onto the Customer.io profile as permanent attributes. `is_impersonating`
            // and `is_staff` describe the actor behind an event, not the person the profile belongs
            // to, so they only belong on `track`/`page`. Merging them into an identify is what put
            // `is_impersonating`/`is_staff` attributes on member profiles.
            const isIdentify = eventType === 'identify';

            // `is_impersonating` is always false now that impersonated sessions send nothing. It stays
            // on the payload so charts filtering on `is_impersonating is False` keep matching.
            const actorProperties = isIdentify ? {} : { is_impersonating: false, is_staff: localViews.isEmployee() };

            const eventProperties: Record<string, unknown> = {
                ...actorProperties,
                user_id: localViews.user()?.pk,
                ...properties,
            };

            if (isIdentify) {
                // Stripped after the merge rather than merely left out before it: `...properties`
                // spreads last, so a caller passing either key would otherwise put it straight back
                // onto the profile as a trait.
                delete eventProperties.is_impersonating;
                delete eventProperties.is_staff;
            }

            window.collectiveTracking?.('sendEvent', eventType, name, eventProperties, analyticsUserId);
        } catch (e) {
            sendDatadogRumError('Error in collectiveTracking.sendEvent', e, {
                contexts: { sendEvent: { eventType, name, properties } },
            });
        }
    },
};
